import { useState, useEffect, useMemo } from 'react';
import { getClients, createClient, updateClient, deleteClient, getPrestataires } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';

const EMPTY_FORM = { nom: '', statut: 'Actif', prestataire_id: '' };

export function useClientsManagement() {
  const { showToast } = useToast();

  const [clients, setClients]           = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [searchTerm, setSearchTerm]     = useState('');

  const [showModal, setShowModal]       = useState(false);
  const [modalMode, setModalMode]       = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting]     = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete]       = useState(null);
  const [deleting, setDeleting]               = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [rawClients, rawPrestataires] = await Promise.all([getClients(), getPrestataires()]);

      setClients((rawClients || []).map(c => ({
        id:             c.id,
        nom:            c.fields?.Nom    || '',
        statut:         c.fields?.Statut || 'Actif',
        prestataire_id: Array.isArray(c.fields?.['Prestataire porteur'])
          ? (c.fields['Prestataire porteur'][0] || null)
          : (c.fields?.['Prestataire porteur'] || null),
      })));

      setPrestataires((rawPrestataires || []).map(p => ({
        id:  p.id,
        nom: p.fields?.Nom || '',
      })));

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const q = searchTerm.toLowerCase();
    return clients.filter(c => c.nom.toLowerCase().includes(q));
  }, [clients, searchTerm]);

  const getPrestataireNom = (id) => {
    if (!id) return '-';
    return prestataires.find(p => p.id === id)?.nom || '-';
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormData(mode === 'edit' && item
      ? { nom: item.nom, statut: item.statut, prestataire_id: item.prestataire_id || '' }
      : EMPTY_FORM
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await createClient(formData);
        showToast('Client créé avec succès', 'success');
      } else {
        await updateClient(selectedItem.id, formData);
        showToast('Client modifié avec succès', 'success');
      }
      await load();
      handleCloseModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteClient(itemToDelete.id);
      showToast('Client supprimé avec succès', 'success');
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  return {
    clients, filteredClients, prestataires, loading, error,
    searchTerm, setSearchTerm,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    getPrestataireNom,
  };
}
